import React from 'react';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import { CardDataState, MetricFormat } from 'containers/Intelligence/types';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import {
  DataStateIcon,
  DataStateLabel,
  MARKER_ICONS,
  markerLabelKey,
  MarkerKind,
  markerTone
} from '../CardDataStateBlock/DataStateMarker';
import { formatMetricReading, NO_VALUE } from '../formatMetric';

/**
 * `lead` is the design system's `display` role, which its own type scale demonstrates with the
 * `82,450` of Customer Population & Activity — the two figures that headline that card's rings are
 * deliberately a step larger than every other reading on the page. Everything else takes `default`,
 * so no other card moves.
 */
export type MetricValueScale = 'default' | 'lead';

export interface IMetricValueProps {
  value: number | null;
  format: MetricFormat;
  /** Symbol or ISO code, meaningful only for `currency`. Threaded down by the page container. */
  currency?: string;
  /** Secondary line giving the value its context, e.g. `of 82,450 total registered users`. */
  denominator?: string;
  scale?: MetricValueScale;
  /**
   * Set when this reading has no catalogue mapping at all, distinct from any other reason a value
   * can be missing (still loading, fetched but uncalculated). Takes the figure's slot as a named
   * state — `Not Configured` plus its marker icon, matching `CardDataStateBlock`'s card-level
   * treatment of the same state — rather than the generic no-reading dash. PLA-8815.
   */
  notConfigured?: boolean;
  /**
   * This tile's OWN outcome, when its metric did not answer with rows. Takes the figure's slot the
   * same way `notConfigured` does, but names which of the three it was — awaiting data, not
   * available here, or a failed request — so a card that has readings elsewhere can keep them and
   * still say what happened to this one. PLA-8828.
   */
  dataState?: CardDataState;
  className?: string;
}

const FIGURE_SIZES: Record<MetricValueScale, string> = {
  default: tokens.type['stat-size'],
  lead: tokens.type['stat-lead-size']
};

const MetricValueWrapper = styled.div`
  display: flex;
  flex-direction: column;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: ${tokens.space['4']};
  }
`;

// Only the size varies by scale: both figure sizes share the stat weight and the stat line box, so a
// lead figure still sits directly above the ring it heads rather than being pushed down by a looser
// leading of its own.
const Figure = styled.span<{ $scale: MetricValueScale }>`
  color: ${tokens.color['text-primary']};
  font-size: ${({ $scale }) => FIGURE_SIZES[$scale]};
  font-weight: ${tokens.type['stat-weight']};
  line-height: ${tokens.type['stat-line']};
`;

const Denominator = styled.span`
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
`;

// Sits in the figure's own slot, at the stat step rather than the display one: a degraded tile is
// never the card's lead reading. `DataStateIcon`/`DataStateLabel` are the SAME components
// `CardDataStateBlock` renders for this state at the card grain — reused rather than re-styled, so
// the two can never visually drift apart the way they did once already. PLA-8815.
const StateMark = styled.span`
  display: inline-flex;
  align-items: center;

  > * + * {
    margin-left: ${tokens.space['8']};
  }
`;

const MetricValue: React.FC<IMetricValueProps> = ({
  value,
  format,
  currency,
  denominator,
  scale = 'default',
  notConfigured = false,
  dataState,
  className
}) => {
  const { t } = useMultiLanguageTranslation();
  // A metric with no value renders a placeholder, never `0` or `0.0%` (FR § Data states); `0` is a
  // real reading and stays visible.
  const hasValue = value !== null && value !== undefined;
  // `notConfigured` stays the shorthand for the one state that predates this prop (no catalogue
  // mapping at all), so every existing caller is unchanged and both routes draw the same mark.
  const markerKind: MarkerKind | undefined =
    dataState && dataState.kind !== 'ok' && dataState.kind !== 'loading'
      ? dataState.kind
      : notConfigured
      ? 'notConfigured'
      : undefined;
  const MarkerIcon = MARKER_ICONS[markerKind ?? 'notConfigured'];

  return (
    <MetricValueWrapper className={className} data-testid="metric-value">
      <Figure $scale={scale} data-testid="metric-value-figure" data-scale={scale}>
        {hasValue ? (
          formatMetricReading(value, format, currency)
        ) : markerKind ? (
          <StateMark data-testid="metric-value-not-configured" data-state={markerKind}>
            <DataStateIcon
              aria-hidden
              data-testid="metric-value-not-configured-icon"
              data-tone={markerTone(markerKind)}
            >
              <MarkerIcon />
            </DataStateIcon>
            <DataStateLabel>
              {t(
                `${TCustomerInsights}.DataStates.${markerLabelKey(
                  markerKind,
                  dataState?.kind === 'insufficientData' ? dataState.reason : undefined
                )}`
              )}
            </DataStateLabel>
          </StateMark>
        ) : (
          NO_VALUE
        )}
      </Figure>
      {denominator ? (
        <Denominator data-testid="metric-value-denominator">{denominator}</Denominator>
      ) : null}
    </MetricValueWrapper>
  );
};

export default MetricValue;
